import { useMemo, useState } from 'react';
import { Box, Typography, InputBase, Link } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { colors } from '@src/styles';
import { fadeInUpSX, staggerChildrenSX } from '@src/styles/animations';
import { NAVIGATION_ITEMS, NavigationPage } from '@src/constants/navigation';
import { getEnvironmentLabel } from '@src/constants/environment';

export type THomeView = {
  onNavigateTo: (page: NavigationPage) => void;
};

const MODULE_HINTS: Record<NavigationPage, string> = {
  home: '',
  automations: 'Canon · event handlers · localhost',
  tasks: 'Abre já filtrado pela sua squad',
  canonicals: 'Definições · schema · ativação por org',
  scripts: 'Upload de planilha · fila de execução',
  runs: 'Consulta pesada · janela de até 30 dias',
  'org-config': 'Em manutenção · sem novos clientes',
};

const monoLabelSX = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '10px',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: colors.brand.textMuted,
} as const;

const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";

export const HomeView = ({ onNavigateTo }: THomeView) => {
  const [searchTerm, setSearchTerm] = useState('');
  const environment = getEnvironmentLabel();

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return NAVIGATION_ITEMS;
    return NAVIGATION_ITEMS.filter(
      (item) => item.label.toLowerCase().includes(term) || (item.description ?? '').toLowerCase().includes(term),
    );
  }, [searchTerm]);

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        backgroundColor: colors.background.primary,
        fontFamily: titleFontFamily,
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          margin: '0 auto',
          padding: { xs: '20px 8px 32px', md: '56px 40px 48px' },
          display: 'flex',
          flexDirection: 'column',
          gap: 4.5,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
          <Box sx={{ flex: '999 1 540px', minWidth: 0, ...fadeInUpSX() }}>
            <Typography sx={{ ...monoLabelSX, color: colors.primary.yellow, fontSize: '11px' }}>
              Serviços internos · Staff Tarken
            </Typography>
            <Typography
              component="h1"
              sx={{
                margin: '14px 0 12px',
                fontFamily: titleFontFamily,
                fontSize: { xs: '40px', md: '54px' },
                fontWeight: 500,
                letterSpacing: '-0.04em',
                lineHeight: 1,
                color: colors.primary.white,
              }}
            >
              Solutions
            </Typography>
            <Typography sx={{ fontSize: '16px', lineHeight: 1.6, color: colors.brand.textSecondary, maxWidth: 580 }}>
              Escolha por onde começar. Cada módulo busca os próprios dados quando você abre — esta tela não consulta
              nenhuma API.
            </Typography>

            <Box
              sx={{
                marginTop: 3,
                maxWidth: 560,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minHeight: 50,
                padding: '0 16px',
                borderRadius: '10px',
                border: `1px solid ${colors.brand.line}`,
                backgroundColor: colors.brand.surface,
              }}
            >
              <SearchIcon sx={{ color: colors.brand.textMuted, fontSize: '1.25rem' }} />
              <InputBase
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Filtrar módulos…"
                inputProps={{ 'aria-label': 'Filtrar módulos' }}
                sx={{
                  flexGrow: 1,
                  color: colors.primary.white,
                  fontFamily: titleFontFamily,
                  fontSize: '14.5px',
                  '& input::placeholder': { color: colors.brand.textMuted, opacity: 1 },
                }}
              />
            </Box>
            <Typography sx={{ marginTop: 1.25, fontSize: '12.5px', color: colors.brand.textMuted }}>
              Busca local, sobre a lista de módulos.
            </Typography>
          </Box>

          <Box
            component="aside"
            sx={{
              flex: '1 1 420px',
              minWidth: 0,
              backgroundColor: colors.brand.surface,
              border: `1px solid ${colors.brand.line}`,
              borderRadius: '12px',
              padding: { xs: '18px 18px', md: '22px 24px' },
              ...fadeInUpSX(80),
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography sx={monoLabelSX}>Ambiente desta sessão</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[
                { label: 'Hub', value: import.meta.env.VITE_HUB_BASE_URL },
                { label: 'Lutien', value: import.meta.env.VITE_LUTIEN_BASE_URL },
                { label: 'Automations', value: import.meta.env.VITE_AUTOMATIONS_BASE_URL },
              ].map((entry) => (
                <Box key={entry.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography sx={{ ...monoLabelSX, letterSpacing: '0.14em' }}>{entry.label}</Typography>
                  <Typography
                    sx={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '11.5px',
                      color: colors.brand.textStrong,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {entry.value ?? '—'}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                borderTop: `1px solid ${colors.brand.line}`,
                paddingTop: 1.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '999px',
                  backgroundColor: environment === 'HML' ? colors.primary.yellow : colors.primary.blue,
                }}
              />
              <Typography sx={{ fontSize: '13px', color: colors.brand.textSecondary }}>
                {environment === 'PROD' ? 'Produção' : environment === 'HML' ? 'Homologação' : 'Local'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1.5 }}>
            <Typography
              component="h2"
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: colors.primary.white,
                flexGrow: 1,
              }}
            >
              Módulos
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, ...staggerChildrenSX('button', 6, 45) }}>
            {filteredItems.map((item) => {
              const IconComponent = item.icon;
              const isLegacy = item.id === 'org-config';
              return (
                <Box
                  key={item.id}
                  component="button"
                  type="button"
                  onClick={() => onNavigateTo(item.id)}
                  sx={{
                    flex: '1 1 340px',
                    minWidth: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                    backgroundColor: isLegacy ? colors.brand.surfaceAlt : colors.brand.surface,
                    border: `1px solid ${colors.brand.line}`,
                    borderRadius: '12px',
                    padding: { xs: '18px', md: '24px' },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    fontFamily: titleFontFamily,
                    transition: 'border-color 0.15s ease, background-color 0.15s ease, transform 0.18s ease',
                    '&:hover': {
                      borderColor: colors.primary.yellow,
                      backgroundColor: colors.brand.surface,
                      transform: 'translateY(-2px)',
                    },
                    '&:active': { transform: 'translateY(0)' },
                    '&:hover .home-card-arrow': { transform: 'translateX(3px)', color: colors.primary.yellow },
                    '&:focus-visible': { outline: `2px solid ${colors.primary.yellow}`, outlineOffset: '2px' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '10px',
                        border: `1px solid ${isLegacy ? colors.brand.line : 'rgba(255, 160, 21, 0.45)'}`,
                        color: isLegacy ? colors.brand.textSecondary : colors.primary.yellow,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '17px',
                        fontWeight: 600,
                        flexGrow: 1,
                        color: isLegacy ? colors.brand.textStrong : colors.primary.white,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <ArrowForwardIcon
                      className="home-card-arrow"
                      sx={{
                        fontSize: 18,
                        color: colors.brand.textMuted,
                        transition: 'transform 0.18s ease, color 0.18s ease',
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '13.5px', color: colors.brand.textSecondary, lineHeight: 1.5 }}>
                    {item.description}
                  </Typography>
                  <Typography
                    sx={{
                      ...monoLabelSX,
                      letterSpacing: '0.14em',
                      color: item.id === 'runs' ? colors.status.pending : colors.brand.textMuted,
                      borderTop: `1px solid ${colors.brand.line}`,
                      paddingTop: 1.5,
                      marginTop: 'auto',
                    }}
                  >
                    {MODULE_HINTS[item.id]}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          component="footer"
          sx={{
            ...fadeInUpSX(300),
            borderTop: `1px solid ${colors.brand.line}`,
            paddingTop: 2.5,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2.5,
          }}
        >
          <Typography sx={{ ...monoLabelSX, letterSpacing: '0.16em', flexGrow: 1 }}>
            Tarken · time de Solutions
          </Typography>
          <Link
            href="https://tarken.atlassian.net/wiki"
            target="_blank"
            rel="noreferrer"
            sx={{ fontSize: '13px', color: colors.brand.textSecondary, textDecorationColor: colors.brand.line }}
          >
            Documentação
          </Link>
          <Link
            href="https://github.com/tarken-ag"
            target="_blank"
            rel="noreferrer"
            sx={{ fontSize: '13px', color: colors.brand.textSecondary, textDecorationColor: colors.brand.line }}
          >
            Repositório
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default HomeView;
